import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDrive,
  RefreshCw,
  AlertTriangle,
  Database,
  Layers,
  Activity,
  Check,
  Copy,
  Info,
  Search,
  ShieldAlert,
  Flame,
  CheckCircle2,
  Table,
  Zap,
  Sliders,
  Sparkles,
  Terminal,
  Clock,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { CustomDropdown } from '../../../components/CustomDropdown ';

export interface PostgresStorageViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface PostgresDatabaseItem {
  database: string;
  name?: string;
  is_system?: boolean;
  size_mb: number;
  xid_pct?: number | null;
  mxid_pct?: number | null;
  xid_age?: number | null;
  dead_tuples?: number | null;
  dead_pct?: number | null;
  last_autovacuum?: string | null;
  last_autovacuum_sec?: number | null;
  temp_mb?: number | null;
  unused_indexes_count?: number | null;
  unused_indexes_mb?: number | null;
  status?: string;
}

export interface PostgresRelationItem {
  database: string;
  relation: string;
  heap_mb: number;
  index_mb: number;
  toast_mb: number;
  total_mb: number;
  name?: string;
  table?: string;
}

export interface PostgresUnusedIndexItem {
  database: string;
  table: string;
  index: string;
  size_mb: number;
  name?: string;
}

export interface PostgresFindingItem {
  severity: string;
  category: string;
  database: string;
  object: string;
  finding: string;
  impact: string;
  remediation: string;
  command?: string;
}

export interface PostgresFindingsData {
  note?: string;
  count?: number;
  by_severity?: Record<string, number>;
  items?: PostgresFindingItem[];
}

export interface PostgresStorageOverviewData {
  kpis?: {
    total_size_mb?: number | null;
    databases_count?: number | null;
    volume_used_pct?: number | null;
    volume_used_gb?: number | null;
    volume_free_gb?: number | null;
    volume_total_gb?: number | null;
    wal_size_mb?: number | null;
    max_wal_size_mb?: number | null;
    wal_pct?: number | null;
    max_xid_pct?: number | null;
    max_mxid_pct?: number | null;
    max_xid_age?: number | null;
    tables_needing_vacuum?: number | null;
    tables_needing_analyze?: number | null;
    open_alerts?: {
      total?: number | null;
      warnings?: number | null;
      critical?: number | null;
    };
  };
  volume?: {
    used_pct?: number | null;
    used_gb?: number | null;
    free_gb?: number | null;
    total_gb?: number | null;
    warn_pct?: number | null;
    crit_pct?: number | null;
  };
  wal?: {
    size_mb?: number | null;
    wal_size_mb?: number | null;
    max_wal_size_mb?: number | null;
    pct?: number | null;
    wal_size_pct_of_max?: number | null;
    archive_mode?: string | null;
    replication_slots?: number | null;
    wal_keep_size_mb?: number | null;
    max_slot_wal_keep_size_mb?: number | null;
    slot_wal_unlimited?: boolean | null;
    archived_count?: number | null;
    archive_failed_count?: number | null;
    last_archived_time?: string | null;
    last_failed_time?: string | null;
    slots?: any[];
  };
  xid?: {
    max_xid_pct?: number | null;
    max_mxid_pct?: number | null;
    max_xid_age?: number | null;
    max_xid_wraparound_pct?: number | null;
    max_mxid_wraparound_pct?: number | null;
    max_mxid_age?: number | null;
    oldest_database?: string | null;
    oldest_table?: string | null;
    freeze_max_age?: number | null;
    headroom_xids?: number | null;
    autovacuum_freeze_max_age?: number | null;
    autovacuum_multixact_freeze_max_age?: number | null;
  };
  config?: {
    autovacuum?: string | null;
    autovacuum_max_workers?: number | null;
    running_workers?: number | null;
    xmin_holders?: number | null;
    prepared_xacts?: number | null;
    archive_mode?: string | null;
    replication_slots?: number | null;
    wal_keep_size?: string | null;
    slot_wal_unlimited?: boolean | null;
    data_checksums?: string | null;
    freeze_max_age_xid?: string | null;
    freeze_max_age_mxid?: string | null;
    oldest_xid_table?: string | null;
    data_directory?: string | null;
    checked_at?: string | null;
  };
  tablespaces?: Array<{
    name: string;
    size_gb?: number | null;
    size_mb?: number | null;
    pct?: number | null;
  }>;
  coverage?: {
    databases_checked?: string | null;
    check_interval?: string | null;
    invalid_indexes?: number | null;
    unused_indexes_count?: number | null;
    unused_indexes_mb?: number | null;
    stats_since?: string | null;
  };
  temp_files?: {
    total_mb?: number | null;
    note?: string | null;
    databases?: Array<{ database: string; temp_mb: number }>;
  };
  databases?: PostgresDatabaseItem[];
  largest_relations?: PostgresRelationItem[];
  unused_indexes?: PostgresUnusedIndexItem[];
  events?: Array<{
    time?: string;
    database?: string;
    event?: string;
    severity?: string;
    details?: string;
  }>;
  findings?: PostgresFindingsData;
}

// Color Palettes
const CHART_PALETTE = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#64748b'];

// Number formatting helpers
const formatSize = (mb: number | null | undefined): string => {
  if (mb === null || mb === undefined || isNaN(mb)) return '—';
  if (mb >= 1024) {
    return `${(mb / 1024).toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
  }
  return `${mb.toLocaleString(undefined, { maximumFractionDigits: 2 })} MB`;
};

const formatPct = (pct: number | null | undefined): string => {
  if (pct === null || pct === undefined || isNaN(pct)) return '—';
  return `${pct.toFixed(2)}%`;
};

const formatNumber = (num: number | null | undefined, decimals = 0): string => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: decimals });
};

// Robust normalizers supporting array tuples and object representations
const normalizeRelation = (r: any): PostgresRelationItem => {
  if (Array.isArray(r)) {
    const db = String(r[0] || '');
    const rel = String(r[1] || '');
    const heap = Number(r[2] || 0);
    const index = Number(r[3] || 0);
    const toast = Number(r[4] || 0);
    return {
      database: db,
      relation: rel,
      heap_mb: heap,
      index_mb: index,
      toast_mb: toast,
      total_mb: heap + index + toast,
    };
  }
  const db = String(r?.database || r?.db || '');
  const schemaPrefix = r?.schema ? `${r.schema}.` : '';
  const rel = String(r?.relation || (r?.table ? `${schemaPrefix}${r.table}` : r?.name || r?.relname || ''));
  const heap = Number(r?.heap_mb ?? r?.data_mb ?? 0);
  const index = Number(r?.index_mb ?? 0);
  const toast = Number(r?.toast_mb ?? 0);
  const total = Number(r?.total_mb ?? (heap + index + toast));
  return {
    database: db,
    relation: rel,
    heap_mb: heap,
    index_mb: index,
    toast_mb: toast,
    total_mb: total,
  };
};

const normalizeUnusedIndex = (u: any): PostgresUnusedIndexItem => {
  if (Array.isArray(u)) {
    return {
      database: String(u[0] || ''),
      table: String(u[1] || ''),
      index: String(u[2] || ''),
      size_mb: Number(u[3] || 0),
    };
  }
  return {
    database: String(u?.database || u?.db || ''),
    table: String(u?.table || u?.tablename || ''),
    index: String(u?.index || u?.indexname || u?.name || ''),
    size_mb: Number(u?.size_mb ?? u?.size ?? u?.mb ?? 0),
  };
};

const normalizeDatabase = (d: any): PostgresDatabaseItem => {
  if (Array.isArray(d)) {
    const lastSec = Number(d[8] || 0);
    const lastAvStr = lastSec > 0 ? `${(lastSec / 86400).toFixed(1)} d ago` : '—';
    return {
      database: String(d[0] || ''),
      is_system: Boolean(d[1]),
      size_mb: Number(d[2] || 0),
      xid_pct: d[3] != null ? Number(d[3]) : null,
      mxid_pct: d[4] != null ? Number(d[4]) : null,
      xid_age: d[5] != null ? Number(d[5]) : null,
      dead_tuples: Number(d[6] || 0),
      dead_pct: Number(d[7] || 0),
      last_autovacuum: lastAvStr,
      last_autovacuum_sec: lastSec,
      temp_mb: Number(d[9] || 0),
      unused_indexes_count: Number(d[10] || 0),
      unused_indexes_mb: Number(d[11] || 0),
      status: 'healthy',
    };
  }
  const lastSec = Number(d?.last_autovacuum_age_sec ?? d?.last_autovacuum_sec ?? 0);
  const lastAvStr = d?.last_autovacuum || (lastSec > 0 ? `${(lastSec / 86400).toFixed(1)} d ago` : '—');
  return {
    database: String(d?.database || d?.name || ''),
    is_system: Boolean(d?.is_system),
    size_mb: Number(d?.size_mb ?? d?.size ?? 0),
    xid_pct: d?.xid_wraparound_pct != null ? Number(d?.xid_wraparound_pct) : (d?.xid_pct != null ? Number(d?.xid_pct) : null),
    mxid_pct: d?.mxid_wraparound_pct != null ? Number(d?.mxid_wraparound_pct) : (d?.mxid_pct != null ? Number(d?.mxid_pct) : null),
    xid_age: d?.xid_age != null ? Number(d?.xid_age) : null,
    dead_tuples: Number(d?.dead_tuples ?? 0),
    dead_pct: Number(d?.dead_tuples_pct ?? d?.dead_pct ?? (d?.dead_tuples ? 5.0 : 0)),
    last_autovacuum: lastAvStr,
    last_autovacuum_sec: lastSec,
    temp_mb: Number(d?.temp_files_mb ?? d?.temp_mb ?? 0),
    unused_indexes_count: Number(d?.unused_indexes ?? d?.unused_indexes_count ?? d?.unused_index_count ?? 0),
    unused_indexes_mb: Number(d?.unused_index_mb ?? d?.unused_indexes_mb ?? d?.unused_mb ?? 0),
    status: d?.status || 'healthy',
  };
};

export const PostgresStorageView: React.FC<PostgresStorageViewProps> = ({
  activeCluster,
  timeRange = '60',
  clientId,
  refreshKey,
  dbType = 'POSTGRES',
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<PostgresStorageOverviewData | null>(null);
  const [error, setError] = useState<string | null>(null);


  // Filters & State
  const [selectedDb, setSelectedDb] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'inventory' | 'relations' | 'unused_indexes' | 'findings' | 'events'>('inventory');
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Copy helper
  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 1800);
  };

  // Fetch PostgreSQL Storage & Maintenance Overview
  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    setError(null);
    try {
      const minutes = parseInt(timeRange || '60', 10);
      const params: any = {
        cluster_id: activeCluster,
        minutes: isNaN(minutes) ? 60 : minutes,
      };
      if (clientId) {
        params.client_id = clientId;
      }

      const res = await baseapi.get('/db_monitor_dashboard/postgres/storage-maintenance/overview', { params });
      if (res?.data) {
        const payload = res.data?.data || res.data;

        const rawDatabases = payload.databases || payload.D || [];
        const rawRelations = payload.largest_relations || payload.relations || payload.R || payload.largest_tables || [];

        // Robust extraction of unused index list from all possible API fields
        const rawUnusedIndexes =
          (Array.isArray(payload.indexes?.unused_list) && payload.indexes.unused_list) ||
          (Array.isArray(payload.unused_list) && payload.unused_list) ||
          (Array.isArray(payload.unused_indexes?.unused_list) && payload.unused_indexes.unused_list) ||
          (Array.isArray(payload.unused_indexes) && payload.unused_indexes) ||
          (Array.isArray(payload.largest_unused_indexes) && payload.largest_unused_indexes) ||
          (Array.isArray(payload.U) && payload.U) ||
          [];

        const normalizedUnused = Array.isArray(rawUnusedIndexes)
          ? rawUnusedIndexes.map(normalizeUnusedIndex)
          : [];

        // Sum up unused index MB and count per database from the unused index items
        const unusedMbByDb: Record<string, number> = {};
        const unusedCountByDb: Record<string, number> = {};
        normalizedUnused.forEach((u: any) => {
          const dbKey = String(u.database || '').toLowerCase().trim();
          if (dbKey) {
            unusedMbByDb[dbKey] = (unusedMbByDb[dbKey] || 0) + Number(u.size_mb || 0);
            unusedCountByDb[dbKey] = (unusedCountByDb[dbKey] || 0) + 1;
          }
        });

        // Normalize databases and backfill unused_indexes_mb from unusedMbByDb if missing or 0
        const normalizedDatabases = (Array.isArray(rawDatabases)
          ? rawDatabases.map(normalizeDatabase)
          : []).map((db) => {
            const key = String(db.database || '').toLowerCase().trim();
            const computedMb = unusedMbByDb[key] || 0;
            const computedCount = unusedCountByDb[key] || 0;
            const existingMb = Number(db.unused_indexes_mb || 0);
            const existingCount = Number(db.unused_indexes_count || 0);

            return {
              ...db,
              unused_indexes_mb: existingMb > 0 ? existingMb : computedMb > 0 ? Number(computedMb.toFixed(2)) : 0,
              unused_indexes_count: existingCount > 0 ? existingCount : computedCount > 0 ? computedCount : 0,
            };
          });

        // Robust extraction of WAL telemetry
        const rawWal = payload.wal || {};
        const walSizeMb = Number(rawWal.wal_size_mb ?? rawWal.size_mb ?? payload.kpis?.wal_size_mb ?? 0);
        const maxWalSizeMb = Number(rawWal.max_wal_size_mb ?? payload.kpis?.max_wal_size_mb ?? 0);
        const walPct = Number(
          rawWal.wal_size_pct_of_max ??
          rawWal.pct ??
          payload.kpis?.wal_pct ??
          (maxWalSizeMb > 0 ? (walSizeMb / maxWalSizeMb) * 100 : 0)
        );
        const replicationSlotsCount = Array.isArray(rawWal.slots)
          ? rawWal.slots.length
          : Number(rawWal.replication_slots ?? 0);
        const walKeepSizeMb = Number(rawWal.wal_keep_size_mb ?? 0);
        const slotWalUnlimited = rawWal.slot_wal_unlimited != null ? Boolean(rawWal.slot_wal_unlimited) : true;
        const archiveMode = String(rawWal.archive_mode || payload.config?.archive_mode || 'off');

        const normalizedWal = {
          ...rawWal,
          size_mb: walSizeMb,
          wal_size_mb: walSizeMb,
          max_wal_size_mb: maxWalSizeMb,
          pct: walPct,
          wal_size_pct_of_max: walPct,
          archive_mode: archiveMode,
          replication_slots: replicationSlotsCount,
          wal_keep_size_mb: walKeepSizeMb,
          max_slot_wal_keep_size_mb: rawWal.max_slot_wal_keep_size_mb ?? null,
          slot_wal_unlimited: slotWalUnlimited,
          slots: Array.isArray(rawWal.slots) ? rawWal.slots : [],
        };

        // Robust extraction of volume telemetry
        const rawVolume = payload.volume || {};
        const volumeUsedPct = Number(rawVolume.used_pct ?? payload.kpis?.volume_used_pct ?? 0);
        const volumeTotalMb = Number(rawVolume.total_mb ?? 0);
        const volumeUsedMb = Number(rawVolume.used_mb ?? 0);
        const volumeFreeMb = Number(rawVolume.free_mb ?? 0);
        const volumeTotalGb = Number(rawVolume.total_gb ?? (volumeTotalMb > 0 ? volumeTotalMb / 1024 : payload.kpis?.volume_total_gb ?? 0));
        const volumeUsedGb = Number(rawVolume.used_gb ?? (volumeUsedMb > 0 ? volumeUsedMb / 1024 : payload.kpis?.volume_used_gb ?? 0));
        const volumeFreeGb = Number(rawVolume.free_gb ?? (volumeFreeMb > 0 ? volumeFreeMb / 1024 : payload.kpis?.volume_free_gb ?? 0));

        const normalizedVolume = {
          ...rawVolume,
          used_pct: volumeUsedPct,
          used_gb: volumeUsedGb,
          free_gb: volumeFreeGb,
          total_gb: volumeTotalGb,
          data_directory: rawVolume.data_directory || payload.config?.data_directory || '',
          warn_pct: Number(rawVolume.warn_pct ?? 80),
          crit_pct: Number(rawVolume.crit_pct ?? 90),
        };

        // Robust extraction of KPIs
        const normalizedKpis = {
          ...(payload.kpis || {}),
          total_size_mb: Number(payload.kpis?.total_size_mb ?? payload.total_size_mb ?? 0),
          databases_count: Number(payload.kpis?.databases_count ?? normalizedDatabases.length),
          volume_used_pct: volumeUsedPct,
          volume_used_gb: volumeUsedGb,
          volume_free_gb: volumeFreeGb,
          volume_total_gb: volumeTotalGb,
          wal_size_mb: walSizeMb,
          max_wal_size_mb: maxWalSizeMb,
          wal_pct: walPct,
          max_xid_pct: Number(payload.wraparound?.max_xid_wraparound_pct ?? payload.xid?.max_xid_pct ?? payload.kpis?.max_xid_wraparound_pct ?? payload.kpis?.max_xid_pct ?? 0),
          max_mxid_pct: Number(payload.wraparound?.max_mxid_wraparound_pct ?? payload.xid?.max_mxid_pct ?? payload.kpis?.max_mxid_pct ?? 0),
          max_xid_age: Number(payload.wraparound?.max_xid_age ?? payload.xid?.max_xid_age ?? payload.kpis?.max_xid_age ?? 0),
          tables_needing_vacuum: Number(payload.vacuum?.tables_needing_vacuum ?? payload.kpis?.tables_needing_vacuum ?? 0),
          tables_needing_analyze: Number(payload.vacuum?.tables_needing_analyze ?? payload.kpis?.tables_needing_analyze ?? 0),
          open_alerts: payload.open_alerts || payload.kpis?.open_alerts || { total: 0, warnings: 0, critical: 0 },
        };

        // Direct normalizer without fallback mock data
        const normalized: PostgresStorageOverviewData = {
          kpis: normalizedKpis,
          volume: normalizedVolume,
          wal: normalizedWal,
          xid: {
            ...(payload.wraparound || payload.xid || {}),
            max_xid_age: Number(payload.wraparound?.max_xid_age ?? payload.xid?.max_xid_age ?? 0),
            max_xid_pct: Number(payload.wraparound?.max_xid_wraparound_pct ?? payload.xid?.max_xid_pct ?? 0),
            max_mxid_pct: Number(payload.wraparound?.max_mxid_wraparound_pct ?? payload.xid?.max_mxid_pct ?? 0),
            max_xid_wraparound_pct: Number(payload.wraparound?.max_xid_wraparound_pct ?? payload.xid?.max_xid_pct ?? 0),
            max_mxid_age: Number(payload.wraparound?.max_mxid_age ?? payload.xid?.max_mxid_age ?? 0),
            max_mxid_wraparound_pct: Number(payload.wraparound?.max_mxid_wraparound_pct ?? payload.xid?.max_mxid_pct ?? 0),
            freeze_max_age: Number(payload.wraparound?.autovacuum_freeze_max_age ?? payload.xid?.freeze_max_age ?? 200000000),
          },
          config: {
            ...(payload.vacuum || {}),
            ...(payload.config || {}),
            autovacuum: payload.vacuum?.autovacuum_on != null ? (payload.vacuum.autovacuum_on ? 'ON' : 'OFF') : (payload.config?.autovacuum ?? 'ON'),
            autovacuum_max_workers: Number(payload.vacuum?.autovacuum_max_workers ?? payload.config?.autovacuum_max_workers ?? 10),
            running_workers: Array.isArray(payload.vacuum?.running) ? payload.vacuum.running.length : Number(payload.config?.running_workers ?? 0),
            xmin_holders: Array.isArray(payload.vacuum?.xmin_holders) ? payload.vacuum.xmin_holders.length : Number(payload.config?.xmin_holders ?? 0),
            prepared_xacts: Number(payload.vacuum?.prepared_xacts ?? payload.config?.prepared_xacts ?? 0),
            archive_mode: archiveMode,
            replication_slots: replicationSlotsCount,
            wal_keep_size: `${walKeepSizeMb} MB`,
            slot_wal_unlimited: slotWalUnlimited,
            data_directory: rawVolume.data_directory || payload.config?.data_directory || '',
            oldest_xid_table: payload.wraparound?.oldest_tables?.[0]
              ? `${payload.wraparound.oldest_tables[0].database || ''} · ${payload.wraparound.oldest_tables[0].table || ''}`
              : (payload.config?.oldest_xid_table || '—'),
          },
          tablespaces: Array.isArray(payload.tablespaces) ? payload.tablespaces : [],
          coverage: {
            ...(payload.coverage || {}),
            unused_indexes_count: payload.coverage?.unused_indexes_count ?? payload.indexes?.unused ?? 0,
            unused_indexes_mb: payload.coverage?.unused_indexes_mb ?? payload.indexes?.unused_mb ?? 0,
          },
          temp_files: payload.temp_files || {
            total_mb: 0,
            total_files: 0,
          },
          databases: normalizedDatabases,
          largest_relations: Array.isArray(rawRelations)
            ? rawRelations.map(normalizeRelation)
            : [],
          unused_indexes: normalizedUnused,
          events: Array.isArray(payload.events) ? payload.events : [],
          findings: payload.findings || { count: 0, items: [] },
        };

        setData(normalized);
      }
    } catch (err: any) {
      console.error('Failed to fetch Postgres storage overview:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load PostgreSQL storage overview');
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Safe data extraction
  const kpis = data?.kpis;
  const volume = data?.volume;
  const wal = data?.wal;
  const xid = data?.xid;
  const config = data?.config;
  const tablespaces = data?.tablespaces || [];
  const coverage = data?.coverage;
  const tempFiles = data?.temp_files;
  const findings = data?.findings;
  const allDatabases = useMemo(() => data?.databases || [], [data?.databases]);
  const allRelations = useMemo(() => data?.largest_relations || [], [data?.largest_relations]);
  const allUnusedIndexes = useMemo(() => data?.unused_indexes || [], [data?.unused_indexes]);
  const events = useMemo(() => data?.events || [], [data?.events]);

  // Dynamic lookup of unused index size by database name
  const unusedMbByDatabase = useMemo(() => {
    const map: Record<string, number> = {};
    allUnusedIndexes.forEach((u) => {
      const dbKey = String(u.database || '').toLowerCase().trim();
      if (dbKey) {
        map[dbKey] = (map[dbKey] || 0) + Number(u.size_mb || 0);
      }
    });
    return map;
  }, [allUnusedIndexes]);

  // List of database names for Dropdown filter
  const databaseOptions = useMemo(() => {
    const names = new Set<string>();
    allDatabases.forEach((d) => {
      if (d?.database) names.add(d.database);
    });
    allRelations.forEach((r) => {
      if (r?.database) names.add(r.database);
    });
    allUnusedIndexes.forEach((u) => {
      if (u?.database) names.add(u.database);
    });
    findings?.items?.forEach((f) => {
      if (f?.database) names.add(f.database);
    });
    return Array.from(names).sort();
  }, [allDatabases, allRelations, allUnusedIndexes, findings]);

  // Dropdown options for CustomDropdown
  const dbDropdownOptions = useMemo(() => [
    { label: `All databases (${databaseOptions.length})`, value: 'all' },
    ...databaseOptions.map((name) => ({ label: name, value: name })),
  ], [databaseOptions]);

  // Filtered databases
  const filteredDatabases = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allDatabases.filter((d) => {
      const dbName = String(d?.database || '').toLowerCase();
      const matchDb = selectedDb === 'all' || dbName === selectedDb.toLowerCase();
      const matchSearch =
        !q ||
        dbName.includes(q) ||
        String(d?.status || '').toLowerCase().includes(q);
      return matchDb && matchSearch;
    });
  }, [allDatabases, selectedDb, searchQuery]);

  // Filtered relations
  const filteredRelations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allRelations.filter((r) => {
      const dbName = String(r?.database || '').toLowerCase();
      const relName = String(r?.relation || '').toLowerCase();
      const matchDb = selectedDb === 'all' || dbName === selectedDb.toLowerCase();
      const matchSearch = !q || relName.includes(q) || dbName.includes(q);
      return matchDb && matchSearch;
    });
  }, [allRelations, selectedDb, searchQuery]);

  // Filtered unused indexes
  const filteredUnusedIndexes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allUnusedIndexes.filter((u) => {
      const dbName = String(u?.database || '').toLowerCase();
      const tableName = String(u?.table || '').toLowerCase();
      const idxName = String(u?.index || '').toLowerCase();
      const matchDb = selectedDb === 'all' || dbName === selectedDb.toLowerCase();
      const matchSearch = !q || idxName.includes(q) || tableName.includes(q) || dbName.includes(q);
      return matchDb && matchSearch;
    });
  }, [allUnusedIndexes, selectedDb, searchQuery]);

  // Filtered findings
  const filteredFindings = useMemo(() => {
    const items = findings?.items || [];
    const q = searchQuery.trim().toLowerCase();
    return items.filter((f) => {
      const matchDb = selectedDb === 'all' || String(f.database || '').toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !q ||
        String(f.finding || '').toLowerCase().includes(q) ||
        String(f.object || '').toLowerCase().includes(q) ||
        String(f.database || '').toLowerCase().includes(q) ||
        String(f.remediation || '').toLowerCase().includes(q) ||
        String(f.category || '').toLowerCase().includes(q) ||
        String(f.impact || '').toLowerCase().includes(q) ||
        String(f.command || '').toLowerCase().includes(q);
      return matchDb && matchSearch;
    });
  }, [findings, selectedDb, searchQuery]);

  // Filtered events
  const filteredEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return events.filter((evt) => {
      const matchDb = selectedDb === 'all' || !evt.database || String(evt.database).toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !q ||
        String(evt.event || '').toLowerCase().includes(q) ||
        String(evt.details || '').toLowerCase().includes(q) ||
        String(evt.database || '').toLowerCase().includes(q);
      return matchDb && matchSearch;
    });
  }, [events, selectedDb, searchQuery]);

  // Database size donut chart data (Top 5 + Others)
  const donutData = useMemo(() => {
    const activeList = allDatabases.filter((d) => selectedDb === 'all' || String(d?.database || '').toLowerCase() === selectedDb.toLowerCase());
    const sorted = [...activeList].sort((a, b) => (b.size_mb || 0) - (a.size_mb || 0));
    const totalSize = sorted.reduce((sum, d) => sum + (d.size_mb || 0), 0);

    if (sorted.length <= 5) {
      return sorted.map((d, index) => ({
        name: d.database || 'db',
        size_mb: d.size_mb,
        pct: totalSize > 0 ? (d.size_mb / totalSize) * 100 : 0,
        color: CHART_PALETTE[index % CHART_PALETTE.length],
      }));
    }

    const top5 = sorted.slice(0, 5).map((d, index) => ({
      name: d.database || 'db',
      size_mb: d.size_mb,
      pct: totalSize > 0 ? (d.size_mb / totalSize) * 100 : 0,
      color: CHART_PALETTE[index % CHART_PALETTE.length],
    }));

    const otherSize = sorted.slice(5).reduce((sum, d) => sum + (d.size_mb || 0), 0);
    top5.push({
      name: `${sorted.length - 5} Smaller DBs`,
      size_mb: otherSize,
      pct: totalSize > 0 ? (otherSize / totalSize) * 100 : 0,
      color: CHART_PALETTE[5],
    });

    return top5;
  }, [allDatabases, selectedDb]);

  // Dead tuples chart data
  const deadTuplesChartData = useMemo(() => {
    return allDatabases
      .filter((d) => (d.dead_tuples || 0) > 0)
      .filter((d) => selectedDb === 'all' || String(d?.database || '').toLowerCase() === selectedDb.toLowerCase())
      .map((d) => ({
        database: d.database,
        dead_pct: d.dead_pct || 0,
        dead_tuples: d.dead_tuples || 0,
      }));
  }, [allDatabases, selectedDb]);

  // Largest relations chart data (top 8)
  const relationsChartData = useMemo(() => {
    return filteredRelations.slice(0, 8).map((r) => {
      const relStr = String(r?.relation || r?.name || '');
      const name = relStr
        ? (relStr.includes('.') ? relStr.split('.').pop()?.replace('_timescaledb_internal.', '') || relStr : relStr)
        : 'relation';
      const heap = Number(r?.heap_mb || 0);
      const index = Number(r?.index_mb || 0);
      const toast = Number(r?.toast_mb || 0);
      const total = Number(r?.total_mb || (heap + index + toast));
      return {
        name,
        fullName: relStr || 'relation',
        database: String(r?.database || ''),
        heap_mb: Number(heap.toFixed(2)),
        index_mb: Number(index.toFixed(2)),
        toast_mb: Number(toast.toFixed(2)),
        total_mb: Number(total.toFixed(2)),
      };
    });
  }, [filteredRelations]);

  // Max XID values
  const maxXidPct = useMemo(() => {
    const list = allDatabases.filter((d) => selectedDb === 'all' || String(d?.database || '').toLowerCase() === selectedDb.toLowerCase());
    if (list.length === 0) return xid?.max_xid_pct || 0;
    return Math.max(...list.map((d) => d.xid_pct || 0), 0);
  }, [allDatabases, selectedDb, xid]);

  const maxMxidPct = useMemo(() => {
    const list = allDatabases.filter((d) => selectedDb === 'all' || String(d?.database || '').toLowerCase() === selectedDb.toLowerCase());
    if (list.length === 0) return xid?.max_mxid_pct || 0;
    return Math.max(...list.map((d) => d.mxid_pct || 0), 0);
  }, [allDatabases, selectedDb, xid]);

  // Volume gauge calculations (Used % arc)
  const volumeUsedPct = volume?.used_pct ?? kpis?.volume_used_pct ?? 0;
  const isVolumeWarn = volumeUsedPct >= (volume?.warn_pct ?? 80);
  const isVolumeCrit = volumeUsedPct >= (volume?.crit_pct ?? 90);

  // Status overall banner pill
  const overallStatus = useMemo(() => {
    const openAlerts = kpis?.open_alerts?.total || 0;
    const criticalAlerts = kpis?.open_alerts?.critical || 0;
    if (criticalAlerts > 0 || isVolumeCrit) {
      return { label: 'CRITICAL', color: 'rose' };
    }
    if (openAlerts > 0 || isVolumeWarn) {
      return { label: 'WARNINGS', color: 'amber' };
    }
    return { label: 'ALL HEALTHY', color: 'emerald' };
  }, [kpis, isVolumeCrit, isVolumeWarn]);

  // Render Loading Skeleton
  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="h-14 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
          <div className="lg:col-span-4 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          <div className="lg:col-span-4 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          <div className="lg:col-span-4 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
        </div>
        <div className="h-96 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
      </div>
    );
  }

  // Render Error State
  if (error && !data) {
    return (
      <div className="p-8 rounded-xl bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
        <div>
          <h3 className="text-base font-semibold text-rose-900 dark:text-rose-200">
            Failed to Load PostgreSQL Storage & Maintenance Overview
          </h3>
          <p className="text-sm text-rose-600 dark:text-rose-400 mt-1 max-w-lg mx-auto">{error}</p>
        </div>
        <button
          onClick={() => fetchData()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold shadow-sm transition-colors cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3.5">
      {/* ─────────────────────────────────────────────────────────────
          TOP BANNER HEADER & CONTROLS
          ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Storage & Maintenance
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
                {(dbType || 'POSTGRESQL').toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-200 mt-0.5">
              Storage capacity, vacuum diagnostics, XID wraparound, WAL telemetry & relation maintenance
            </p>
          </div>
        </div>

        {/* Controls: Status pill, Database Selector, Reset & Refresh */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Status Badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm ${overallStatus.color === 'rose'
                ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-500/30'
                : overallStatus.color === 'amber'
                  ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                  : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
              }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${overallStatus.color === 'rose'
                  ? 'bg-rose-500 animate-pulse'
                  : overallStatus.color === 'amber'
                    ? 'bg-amber-500'
                    : 'bg-emerald-500'
                }`}
            />
            {overallStatus.label}
          </span>

          {/* Database Filter CustomDropdown */}
          <CustomDropdown
            data={dbDropdownOptions}
            selectedValue={selectedDb}
            onSelect={(val) => setSelectedDb(val)}
            width="w-48 sm:w-56"
            placeholder="Select database"
            buttonHeight={8}
          />

          {/* Reset Button */}
          {selectedDb !== 'all' && (
            <button
              onClick={() => {
                setSelectedDb('all');
                setSearchQuery('');
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700 transition-colors cursor-pointer"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 0: 6 TOP KPI CARDS
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {/* KPI 1: Total Size */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Total size</span>
            <Database className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatSize(kpis?.total_size_mb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {allDatabases.length} databases · {formatNumber(kpis?.total_size_mb, 1)} MB
          </div>
        </div>

        {/* KPI 2: Volume Used */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Volume used</span>
            <HardDrive className={`w-3.5 h-3.5 ${isVolumeWarn ? 'text-amber-500' : 'text-blue-500'}`} />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatPct(volumeUsedPct)}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 mb-1 flex justify-between">
              <span>warn 80%</span>
              <span className="font-semibold text-rose-500">crit 90%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${isVolumeCrit ? 'bg-rose-500' : isVolumeWarn ? 'bg-amber-500' : 'bg-blue-500'
                  }`}
                style={{ width: `${Math.min(volumeUsedPct || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* KPI 3: WAL Size */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>WAL size</span>
            <Activity className="w-3.5 h-3.5 text-violet-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatSize(wal?.wal_size_mb ?? wal?.size_mb ?? kpis?.wal_size_mb)}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 mb-1 truncate">
              {formatPct(wal?.wal_size_pct_of_max ?? wal?.pct ?? kpis?.wal_pct)} of {formatSize(wal?.max_wal_size_mb ?? kpis?.max_wal_size_mb)} max
            </div>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-violet-500 transition-all duration-500"
                style={{ width: `${Math.min(wal?.wal_size_pct_of_max ?? wal?.pct ?? kpis?.wal_pct ?? 0, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* KPI 4: Max XID Wraparound */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Max XID wrap</span>
            <Flame className="w-3.5 h-3.5 text-emerald-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatPct(maxXidPct)}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 mb-1 flex justify-between">
              <span>warn 50%</span>
              <span>crit 75%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${maxXidPct >= 75 ? 'bg-rose-500' : maxXidPct >= 50 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                style={{ width: `${Math.min(maxXidPct || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* KPI 5: Tables Needing Vacuum */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Needing vacuum</span>
            <Zap className="w-3.5 h-3.5 text-cyan-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {kpis?.tables_needing_vacuum ?? 0}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {kpis?.tables_needing_analyze ?? 0} needing analyze
          </div>
        </div>

        {/* KPI 6: Open Alerts */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Open alerts</span>
            <ShieldAlert className="w-3.5 h-3.5 text-emerald-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {kpis?.open_alerts?.total ?? 0}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200">
            <span className="text-amber-500 font-semibold">{kpis?.open_alerts?.warnings ?? 0} warn</span> ·{' '}
            <span className="text-emerald-500 font-semibold">{kpis?.open_alerts?.critical ?? 0} crit</span>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 1: DATABASE SIZE DONUT, XID WRAPAROUND & VOLUME USAGE GAUGE
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Card 1: Database Size Donut Breakdown */}
        <div className="lg:col-span-5 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-blue-500" />
              Database Size Breakdown
            </h3>
            <span className="text-[11px] font-mono text-gray-500 dark:text-gray-200">
              {formatSize(kpis?.total_size_mb)}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 my-auto">
            {/* Recharts Donut Pie */}
            <div className="relative w-36 h-36 flex-shrink-0 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={62}
                    
                    dataKey="size_mb"
                  >
                    {donutData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} stroke="none"  />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(val: any, name: any) => [
                      `${formatSize(val)} (${donutData.find(d => d.name === name)?.pct.toFixed(1)}%)`,
                      name
                    ]}
                    contentStyle={{
                      backgroundColor: 'rgba(17, 24, 39, 1)',
                      borderColor: '#374151',
                      borderRadius: '8px',
                      fontSize: '11px',
                      color: '#ffffff',
                    }}
                    itemStyle={{ color: '#ffffff' }}
                    labelStyle={{ color: '#ffffff' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs font-bold text-gray-900 dark:text-white font-mono leading-tight">
                  {kpis?.total_size_mb && kpis.total_size_mb >= 1024
                    ? (kpis.total_size_mb / 1024).toFixed(1)
                    : (kpis?.total_size_mb || 0).toFixed(0)}
                </span>
                <span className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-200">
                  {kpis?.total_size_mb && kpis.total_size_mb >= 1024 ? 'GB' : 'MB'}
                </span>
              </div>
            </div>

            {/* Top Database List with color dots */}
            <div className="flex-1 w-full space-y-1.5 overflow-hidden">
              {donutData.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-xs gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="font-medium text-gray-800 dark:text-gray-200 truncate" title={item.name}>
                      {item.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 font-mono">
                    <span className="text-gray-900 dark:text-white font-semibold">{formatSize(item.size_mb)}</span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-200 w-10 text-right">{item.pct.toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Card 2: XID Wraparound Telemetry */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Flame className="w-3.5 h-3.5 text-amber-500" />
              XID Wraparound
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">% of 2.15 B limit</span>
          </div>

          <div className="space-y-3.5 my-auto">
            {/* Zone bar for XID age */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600 dark:text-gray-300 font-medium">XID age, highest database</span>
                <span className="font-mono font-bold text-gray-900 dark:text-white">{formatPct(maxXidPct)}</span>
              </div>
              <div className="relative h-3 rounded-md bg-gradient-to-r from-emerald-500/40 via-amber-500/40 to-rose-500/40 border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div
                  className="absolute top-0 bottom-0 w-1.5 bg-gray-900 dark:bg-white rounded-full shadow -translate-x-1/2 transition-all duration-300"
                  style={{ left: `${Math.min(Math.max(maxXidPct || 1, 1), 100)}%` }}
                />
              </div>
              <div className="relative h-3 mt-1 text-[9px] text-gray-500 dark:text-gray-200 font-mono">
                <span className="absolute left-0">0%</span>
                <span className="absolute left-1/2 -translate-x-1/2">50% warn</span>
                <span className="absolute left-[75%] -translate-x-1/2">75% crit</span>
                <span className="absolute right-0">100%</span>
              </div>
            </div>

            {/* Multixact Age */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600 dark:text-gray-300">Multixact age (MXID)</span>
                <span className="font-mono font-semibold text-gray-900 dark:text-white">{formatPct(maxMxidPct)}</span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${Math.min(maxMxidPct || 0, 100)}%` }} />
              </div>
            </div>

            {/* XID vs autovacuum freeze (200 M threshold) */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600 dark:text-gray-300">XID age vs autovacuum freeze (200 M)</span>
                <span className="font-mono font-semibold text-gray-900 dark:text-white">
                  {formatPct(((xid?.max_xid_age || 59228772) / 200000000) * 100)}
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full"
                  style={{ width: `${Math.min(((xid?.max_xid_age || 59228772) / 200000000) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Meta Rows */}
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-200">Max XID age · database</span>
                <span className="font-medium text-gray-800 dark:text-gray-200 font-mono">
                  {((xid?.max_xid_age || 59228772) / 1000000).toFixed(1)} M · {xid?.oldest_database || 'EBO'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-200">Headroom to wraparound</span>
                <span className="font-medium text-gray-800 dark:text-gray-200 font-mono">
                  {((2147483648 - (xid?.max_xid_age || 59228772)) / 1000000000).toFixed(2)} B XIDs
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Volume Usage Radial Gauge */}
        <div className="lg:col-span-3 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <HardDrive className="w-3.5 h-3.5 text-blue-500" />
              Volume Usage
            </h3>
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold ${isVolumeCrit
                  ? 'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400'
                  : isVolumeWarn
                    ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'
                    : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400'
                }`}
            >
              {formatPct(volumeUsedPct)}
            </span>
          </div>

          {/* SVG Semicircle Gauge */}
          <div className="relative flex flex-col items-center justify-center my-1">
            <svg viewBox="0 0 120 72" className="w-36 h-22 overflow-visible">
              {/* Background Arc */}
              <path
                d="M10 62A50 50 0 0 1 110 62"
                fill="none"
                stroke="currentColor"
                className="text-gray-200 dark:text-gray-700"
                strokeWidth="12"
                strokeLinecap="round"
                pathLength="100"
              />
              {/* Value Arc */}
              <path
                d="M10 62A50 50 0 0 1 110 62"
                fill="none"
                stroke={isVolumeCrit ? '#ef4444' : isVolumeWarn ? '#f59e0b' : '#3b82f6'}
                strokeWidth="12"
                strokeLinecap="round"
                pathLength="100"
                strokeDasharray={`${Math.min(volumeUsedPct, 100)} 100`}
                className="transition-all duration-700 ease-out"
              />
              {/* Threshold Ticks */}
              <path
                d="M10 62A50 50 0 0 1 110 62"
                fill="none"
                stroke="#f59e0b"
                strokeWidth="14"
                pathLength="100"
                strokeDasharray="0.8 100"
                strokeDashoffset="-80"
              />
              <path
                d="M10 62A50 50 0 0 1 110 62"
                fill="none"
                stroke="#ef4444"
                strokeWidth="14"
                pathLength="100"
                strokeDasharray="0.8 100"
                strokeDashoffset="-90"
              />
              <text
                x="60"
                y="56"
                textAnchor="middle"
                className="text-base font-bold fill-gray-900 dark:fill-white font-mono"
              >
                {formatPct(volumeUsedPct)}
              </text>
            </svg>
          </div>

          {/* Volume Details Rows */}
          <div className="space-y-1.5 pt-2 border-t border-gray-100 dark:border-gray-700/60 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">Used</span>
              <span className="font-semibold text-gray-900 dark:text-white font-mono">
                {volume?.used_gb?.toFixed(2) || '234.34'} GB
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">Free</span>
              <span className="font-semibold text-emerald-600 dark:text-emerald-400 font-mono">
                {volume?.free_gb?.toFixed(2) || '75.60'} GB
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">Total</span>
              <span className="font-semibold text-gray-800 dark:text-gray-200 font-mono">
                {volume?.total_gb?.toFixed(2) || '309.95'} GB
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 2: DEAD TUPLES, UNUSED INDEXES & CONFIG CARDS
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Card 1: Dead Tuples % & Count */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Flame className="w-3.5 h-3.5 text-amber-500" />
              Dead Tuples Ratio
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">% of live+dead</span>
          </div>

          <div className="my-auto space-y-2">
            {deadTuplesChartData.length === 0 ? (
              <div className="py-8 text-center text-xs text-gray-500 dark:text-gray-200">No dead tuples reported</div>
            ) : (
              <div className="space-y-2.5 max-h-[160px] overflow-y-auto pr-1">
                {deadTuplesChartData.map((d, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{d.database}</span>
                      <div className="flex items-center gap-2 font-mono">
                        <span className="text-gray-500 dark:text-gray-200 text-[11px]">{formatNumber(d.dead_tuples)} tuples</span>
                        <span className={`font-semibold ${d.dead_pct >= 50 ? 'text-amber-500' : 'text-blue-500'}`}>
                          {d.dead_pct.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${d.dead_pct >= 50 ? 'bg-amber-500' : 'bg-blue-500'}`}
                        style={{ width: `${Math.min(d.dead_pct, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Diagnostic Note */}
          <div className="mt-3 p-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-[11px] text-amber-800 dark:text-amber-300">
            High percentages on very low counts (e.g. 16 dead tuples) are non-critical. Autovacuum cleans active relations automatically.
          </div>
        </div>

        {/* Card 2: Unused Indexes by Database */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-violet-500" />
              Unused Indexes
            </h3>
            <span className="text-[11px] font-mono text-gray-500 dark:text-gray-200">size · count</span>
          </div>

          <div className="my-auto space-y-2 max-h-[190px] overflow-y-auto pr-1">
            {allDatabases
              .filter((d) => (d.unused_indexes_count || 0) > 0 || (d.unused_indexes_mb || 0) > 0 || (unusedMbByDatabase[String(d?.database || '').toLowerCase().trim()] || 0) > 0)
              .filter((d) => selectedDb === 'all' || String(d?.database || '').toLowerCase() === selectedDb.toLowerCase())
              .map((d, idx) => {
                const dbKey = String(d?.database || '').toLowerCase().trim();
                const mb = (d.unused_indexes_mb && d.unused_indexes_mb > 0)
                  ? d.unused_indexes_mb
                  : (unusedMbByDatabase[dbKey] || 0);
                const count = (d.unused_indexes_count && d.unused_indexes_count > 0)
                  ? d.unused_indexes_count
                  : allUnusedIndexes.filter((u) => String(u.database || '').toLowerCase().trim() === dbKey).length;

                const maxUnusedMb = Math.max(
                  ...allDatabases.map((db) => {
                    const k = String(db?.database || '').toLowerCase().trim();
                    return (db.unused_indexes_mb && db.unused_indexes_mb > 0) ? db.unused_indexes_mb : (unusedMbByDatabase[k] || 0);
                  }),
                  1
                );
                const pct = maxUnusedMb > 0 ? (mb / maxUnusedMb) * 100 : 0;
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{d.database}</span>
                      <span className="font-mono text-gray-900 dark:text-white font-semibold">
                        {formatSize(mb)} · {count} idx
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full" style={{ width: `${Math.max(pct, 2)}%` }} />
                    </div>
                  </div>
                );
              })}
          </div>

          <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700/60">
            Total reported: <span className="font-semibold text-gray-700 dark:text-gray-300">{coverage?.unused_indexes_count ?? 0} unused</span> ({formatSize(coverage?.unused_indexes_mb ?? 0)})
          </div>
        </div>

        {/* Card 3: WAL, Vacuum & Engine Configuration */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Sliders className="w-3.5 h-3.5 text-blue-500" />
              Engine & Vacuum Config
            </h3>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
              OK
            </span>
          </div>

          <div className="space-y-2 text-xs">
            {/* WAL Progress */}
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500 dark:text-gray-200">WAL vs max_wal_size</span>
                <span className="font-mono font-semibold text-gray-900 dark:text-white">
                  {formatSize(wal?.wal_size_mb ?? wal?.size_mb ?? kpis?.wal_size_mb)} / {formatSize(wal?.max_wal_size_mb ?? kpis?.max_wal_size_mb)}
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-500 rounded-full"
                  style={{ width: `${Math.min(wal?.wal_size_pct_of_max ?? wal?.pct ?? kpis?.wal_pct ?? 0, 100)}%` }}
                />
              </div>
            </div>

            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-gray-500 dark:text-gray-200">Autovacuum · max workers</span>
              <span className="font-semibold text-emerald-600 dark:text-emerald-400 font-mono">
                {config?.autovacuum || 'ON'} · {config?.autovacuum_max_workers || 10}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-gray-500 dark:text-gray-200">Running workers · xmin · prepared</span>
              <span className="font-mono text-gray-800 dark:text-gray-200">
                {config?.running_workers ?? 0} · {config?.xmin_holders ?? 0} · {config?.prepared_xacts ?? 0}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-gray-500 dark:text-gray-200">Archive mode · replication slots</span>
              <span className="font-mono text-gray-800 dark:text-gray-200">
                {wal?.archive_mode || config?.archive_mode || 'off'} · {wal?.replication_slots ?? config?.replication_slots ?? 0}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-gray-500 dark:text-gray-200">wal_keep_size · slot unlimited</span>
              <span className="font-mono text-gray-800 dark:text-gray-200">
                {wal?.wal_keep_size_mb != null ? `${wal.wal_keep_size_mb} MB` : (config?.wal_keep_size || '0 MB')} ·{' '}
                <span className={wal?.slot_wal_unlimited ?? config?.slot_wal_unlimited ? 'text-amber-500 font-semibold' : 'text-gray-600 dark:text-gray-300'}>
                  {String(wal?.slot_wal_unlimited ?? config?.slot_wal_unlimited ?? false)}
                </span>
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-500 dark:text-gray-200">Oldest XID table</span>
              <span className="font-mono text-gray-800 dark:text-gray-200 truncate max-w-[170px]" title={config?.oldest_xid_table || ''}>
                {config?.oldest_xid_table || 'EBO · pg_toast_1255'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 3: LARGEST RELATIONS, TEMP FILES & TABLESPACES
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Card 1: Largest Relations Breakdown (Top 8 horizontal bars) */}
        <div className="lg:col-span-5 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Table className="w-3.5 h-3.5 text-blue-500" />
              Largest Relations
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">top 10 reported</span>
          </div>

          <div className="space-y-2.5 max-h-[220px] overflow-y-auto pr-1 my-auto">
            {relationsChartData.length === 0 ? (
              <div className="py-8 text-center text-xs text-gray-500 dark:text-gray-200">No relation data available</div>
            ) : (
              relationsChartData.map((r, idx) => {
                const maxTotal = relationsChartData[0]?.total_mb || 1;
                const totalPct = (r.total_mb / maxTotal) * 100;
                const heapPct = r.total_mb > 0 ? (r.heap_mb / r.total_mb) * 100 : 0;
                const indexPct = r.total_mb > 0 ? (r.index_mb / r.total_mb) * 100 : 0;
                const toastPct = r.total_mb > 0 ? (r.toast_mb / r.total_mb) * 100 : 0;

                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-gray-800 dark:text-gray-200 truncate max-w-[200px]" title={r.fullName}>
                        {r.name} <span className="text-[10px] text-gray-200 dark:text-gray-500">({r.database})</span>
                      </span>
                      <span className="font-mono text-gray-900 dark:text-white font-semibold">
                        {formatSize(r.total_mb)}
                      </span>
                    </div>
                    {/* Multi-segment stacked bar */}
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
                      <div style={{ width: `${totalPct}%` }} className="h-full flex rounded-full overflow-hidden">
                        <div style={{ width: `${heapPct}%` }} className="h-full bg-blue-500" title={`Heap: ${formatSize(r.heap_mb)}`} />
                        <div style={{ width: `${indexPct}%` }} className="h-full bg-violet-500" title={`Index: ${formatSize(r.index_mb)}`} />
                        <div style={{ width: `${toastPct}%` }} className="h-full bg-amber-500" title={`TOAST: ${formatSize(r.toast_mb)}`} />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-gray-700/60 text-[11px] text-gray-500 dark:text-gray-200">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Heap
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-violet-500" /> Index
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> TOAST
            </span>
          </div>
        </div>

        {/* Card 2: Temp Files Written */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-amber-500" />
              Temp Files Activity
            </h3>
            <span className="text-[11px] font-mono text-gray-500 dark:text-gray-200">MB</span>
          </div>

          <div className="my-auto space-y-2">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[11px] text-gray-500 dark:text-gray-200">Total temp written</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white font-mono">
                {formatSize(tempFiles?.total_mb ?? 0)}
              </span>
            </div>

            <div className="space-y-2 max-h-[140px] overflow-y-auto pr-1">
              {(tempFiles?.databases || [])
                .filter((d) => selectedDb === 'all' || String(d?.database || '').toLowerCase() === selectedDb.toLowerCase())
                .slice(0, 5)
                .map((d, idx) => {
                  const maxTemp = tempFiles?.databases?.[0]?.temp_mb || 1;
                  const pct = maxTemp > 0 ? (d.temp_mb / maxTemp) * 100 : 0;
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-800 dark:text-gray-200 truncate">{d.database}</span>
                        <span className="font-mono text-gray-900 dark:text-white font-semibold">
                          {formatSize(d.temp_mb)}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.max(pct, 1)}%` }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="mt-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-[11px] text-gray-600 dark:text-gray-300">
            {tempFiles?.note || 'Consider reviewing work_mem for large sorts or complex joins spilling to disk.'}
          </div>
        </div>

        {/* Card 3: Tablespaces & Coverage */}
        <div className="lg:col-span-3 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-blue-500" />
              Tablespaces & Coverage
            </h3>
          </div>

          <div className="space-y-2 text-xs my-auto">
            {/* Tablespaces */}
            {tablespaces.map((ts, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-800 dark:text-gray-200">{ts.name}</span>
                  <span className="font-mono text-gray-900 dark:text-white font-semibold">
                    {ts.size_gb ? `${ts.size_gb} GB` : formatSize(ts.size_mb)}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${idx === 0 ? 'bg-blue-500' : 'bg-cyan-500'}`}
                    style={{ width: `${Math.min(ts.pct || 1, 100)}%` }}
                  />
                </div>
              </div>
            ))}

            <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-200">Databases checked</span>
                <span className="font-mono font-semibold text-gray-900 dark:text-white">{coverage?.databases_checked || '11 / 11'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-200">Check interval</span>
                <span className="font-mono text-gray-800 dark:text-gray-200">{coverage?.check_interval || '1 hour'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-200">Invalid indexes</span>
                <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                  {coverage?.invalid_indexes ?? 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-200">Stats collected since</span>
                <span className="font-mono text-[11px] text-gray-600 dark:text-gray-300">
                  {coverage?.stats_since || '18 Aug 2026'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 4: INTERACTIVE DATA TABLES / TABS EXPLORER
          ───────────────────────────────────────────────────────────── */}
      <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        {/* Navigation Sub-Tabs */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 pt-3 pb-0 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('inventory')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'inventory'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Database className="w-3.5 h-3.5" />
              Database Inventory ({filteredDatabases.length})
            </button>

            <button
              onClick={() => setActiveTab('relations')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'relations'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Table className="w-3.5 h-3.5" />
              Largest Relations ({filteredRelations.length})
            </button>

            <button
              onClick={() => setActiveTab('unused_indexes')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'unused_indexes'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              Largest Unused Indexes ({filteredUnusedIndexes.length})
            </button>

            <button
              onClick={() => setActiveTab('findings')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'findings'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              Automated Findings ({findings?.count ?? filteredFindings.length})
            </button>

            {events.length > 0 && (
              <button
                onClick={() => setActiveTab('events')}
                className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  activeTab === 'events'
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                    : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                Recent Events ({events.length})
              </button>
            )}
          </div>
        </div>

        {/* ── SUB-TAB 1: DATABASE INVENTORY ── */}
        {activeTab === 'inventory' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter database name or status..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200">
                Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredDatabases.length}</span> database(s)
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                  <th className="py-2.5 px-3 font-semibold">Database</th>
                  <th className="py-2.5 px-3 font-semibold">Size</th>
                  <th className="py-2.5 px-3 font-semibold">XID %</th>
                  <th className="py-2.5 px-3 font-semibold">MXID %</th>
                  <th className="py-2.5 px-3 font-semibold">Dead Tuples</th>
                  <th className="py-2.5 px-3 font-semibold">Dead %</th>
                  <th className="py-2.5 px-3 font-semibold">Last Autovacuum</th>
                  <th className="py-2.5 px-3 font-semibold">Temp Files</th>
                  <th className="py-2.5 px-3 font-semibold">Unused Indexes</th>
                  <th className="py-2.5 px-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredDatabases.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-gray-500 dark:text-gray-200">
                      No databases match the filter
                    </td>
                  </tr>
                ) : (
                  filteredDatabases.map((db, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors"
                    >
                      <td className="py-2 px-3 font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <span>{db.database}</span>
                        {db.is_system && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            system
                          </span>
                        )}
                        <button
                          onClick={() => handleCopy(db.database)}
                          className="text-gray-200 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                          title="Copy database name"
                        >
                          {copiedText === db.database ? (
                            <Check className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </td>
                      <td className="py-2 px-3 font-mono font-semibold text-gray-900 dark:text-white">
                        {formatSize(db.size_mb)}
                      </td>
                      <td className="py-2 px-3 font-mono">{formatPct(db.xid_pct)}</td>
                      <td className="py-2 px-3 font-mono">{formatPct(db.mxid_pct)}</td>
                      <td className="py-2 px-3 font-mono">{formatNumber(db.dead_tuples)}</td>
                      <td className="py-2 px-3 font-mono">
                        <span className={db.dead_pct && db.dead_pct >= 50 ? 'text-amber-500 font-semibold' : ''}>
                          {formatPct(db.dead_pct)}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-300">
                        {db.last_autovacuum || '—'}
                      </td>
                      <td className="py-2 px-3 font-mono">{formatSize(db.temp_mb)}</td>
                      <td className="py-2 px-3 font-mono">
                        {(() => {
                          const dbKey = String(db.database || '').toLowerCase().trim();
                          const mb = (db.unused_indexes_mb && db.unused_indexes_mb > 0)
                            ? db.unused_indexes_mb
                            : (unusedMbByDatabase[dbKey] || 0);
                          const count = (db.unused_indexes_count && db.unused_indexes_count > 0)
                            ? db.unused_indexes_count
                            : allUnusedIndexes.filter((u) => String(u.database || '').toLowerCase().trim() === dbKey).length;

                          return count > 0 || mb > 0 ? (
                            <span className="text-violet-600 dark:text-violet-400 font-semibold">
                              {count} ({formatSize(mb)})
                            </span>
                          ) : (
                            <span className="text-gray-200">0</span>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                          <CheckCircle2 className="w-2.5 h-2.5" />
                          HEALTHY
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {/* ── SUB-TAB 2: LARGEST RELATIONS ── */}
        {activeTab === 'relations' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter relation or database name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200">
                Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredRelations.length}</span> relation(s)
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                    <th className="py-2.5 px-3 font-semibold">Database</th>
                  <th className="py-2.5 px-3 font-semibold">Relation</th>
                  <th className="py-2.5 px-3 font-semibold">Heap Size</th>
                  <th className="py-2.5 px-3 font-semibold">Index Size</th>
                  <th className="py-2.5 px-3 font-semibold">TOAST Size</th>
                  <th className="py-2.5 px-3 font-semibold">Total Size</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredRelations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500 dark:text-gray-200">
                      No relations found
                    </td>
                  </tr>
                ) : (
                  filteredRelations.map((r, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">
                        {r.database}
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-800 dark:text-gray-200 flex items-center gap-2">
                        <span>{r.relation}</span>
                        <button
                          onClick={() => handleCopy(r.relation)}
                          className="text-gray-200 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                          title="Copy relation name"
                        >
                          {copiedText === r.relation ? (
                            <Check className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </td>
                      <td className="py-2 px-3 font-mono text-blue-600 dark:text-blue-400">
                        {formatSize(r.heap_mb)}
                      </td>
                      <td className="py-2 px-3 font-mono text-violet-600 dark:text-violet-400">
                        {formatSize(r.index_mb)}
                      </td>
                      <td className="py-2 px-3 font-mono text-amber-600 dark:text-amber-400">
                        {formatSize(r.toast_mb)}
                      </td>
                      <td className="py-2 px-3 font-mono font-bold text-gray-900 dark:text-white">
                        {formatSize(r.total_mb)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {/* ── SUB-TAB 3: LARGEST UNUSED INDEXES ── */}
        {activeTab === 'unused_indexes' && (
          <div className="p-4 space-y-4">
            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2.5">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
              <p className="leading-relaxed text-[11px] opacity-90">
                Counts only activity on this node since stats reset. An index used exclusively on a read replica will read as unused here. Verify on all replica nodes before dropping!
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter unused index, table or database name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200">
                Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredUnusedIndexes.length}</span> unused index(es)
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                  <th className="py-2.5 px-3 font-semibold">Database</th>
                  <th className="py-2.5 px-3 font-semibold">Table</th>
                  <th className="py-2.5 px-3 font-semibold">Unused Index</th>
                  <th className="py-2.5 px-3 font-semibold">Size (MB)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredUnusedIndexes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-gray-500 dark:text-gray-200">
                      No unused indexes reported
                    </td>
                  </tr>
                ) : (
                  filteredUnusedIndexes.map((u, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">
                        {u.database}
                      </td>
                      <td className="py-2 px-3 text-gray-800 dark:text-gray-200">
                        {u.table}
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <span>{u.index}</span>
                        <button
                          onClick={() => handleCopy(u.index)}
                          className="text-gray-200 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                          title="Copy index name"
                        >
                          {copiedText === u.index ? (
                            <Check className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </td>
                      <td className="py-2 px-3 font-mono font-semibold text-violet-600 dark:text-violet-400">
                        {formatSize(u.size_mb)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {/* ── SUB-TAB 4: AUTOMATED FINDINGS ── */}
        {activeTab === 'findings' && (
          <div className="p-4 space-y-4">
            {/* Advisory Note */}
            {findings?.note && (
              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="font-semibold text-amber-900 dark:text-amber-100">Advisory Notice:</span>
                  <p className="leading-relaxed text-[11px] opacity-90">{findings.note}</p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter findings by text, object or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {findings?.by_severity && Object.entries(findings.by_severity).map(([sev, count]) => {
                  const color =
                    sev === 'critical'
                      ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                      : sev === 'warning'
                        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                        : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
                  return (
                    <span key={sev} className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${color}`}
                    >
                      {count} {sev}
                    </span>
                  );
                })}
                <span className="text-xs text-gray-500 dark:text-gray-200">
                  Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredFindings.length}</span> of {findings?.count ?? filteredFindings.length}
                </span>
              </div>
            </div>

            {/* Findings List */}
            {filteredFindings.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-500 dark:text-gray-200">
                No automated findings matching the current filters.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFindings.map((item, idx) => {
                  const sevColor =
                    item.severity === 'critical'
                      ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 border-rose-200 dark:border-rose-500/30'
                      : item.severity === 'warning'
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                        : 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';

                  return (
                    <div
                      key={idx}
                      className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-all space-y-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${sevColor}`}>
                            {item.severity}
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {item.category?.replace(/_/g, ' ')}
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-medium bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20">
                            {item.database}
                          </span>
                        </div>
                        <span className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300 truncate max-w-md">
                          {item.object}
                        </span>
                      </div>

                      <div className="space-y-1.5 text-xs">
                        <div className="text-gray-900 dark:text-white font-medium leading-relaxed">
                          {item.finding}
                        </div>
                        {item.impact && (
                          <div className="text-gray-600 dark:text-gray-300 text-[11px] flex items-start gap-1.5">
                            <span className="font-semibold text-gray-500 dark:text-gray-200 min-w-[55px]">Impact:</span>
                            <span>{item.impact}</span>
                          </div>
                        )}
                        {item.remediation && (
                          <div className="text-gray-600 dark:text-gray-300 text-[11px] flex items-start gap-1.5">
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400 min-w-[55px]">Remedy:</span>
                            <span>{item.remediation}</span>
                          </div>
                        )}
                      </div>

                      {item.command && (
                        <div className="pt-1">
                          <div className="flex items-center justify-between bg-gray-900 text-gray-100 dark:bg-black/60 rounded-lg p-2.5 font-mono text-[11px] border border-gray-700/60 overflow-x-auto">
                            <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                              <Terminal className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                              <span className="truncate select-all">{item.command}</span>
                            </div>
                            <button
                              onClick={() => handleCopy(item.command || '')}
                              className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-[10px] font-medium flex-shrink-0 cursor-pointer"
                              title="Copy command to clipboard"
                            >
                              {copiedText === item.command ? (
                                <>
                                  <Check className="w-3 h-3 text-emerald-400" />
                                  <span className="text-emerald-400">Copied</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── SUB-TAB 5: ALERTS & EVENTS ── */}
        {activeTab === 'events' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200">
                Total Events: <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredEvents.length}</span>
              </div>
            </div>

            {filteredEvents.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-500 dark:text-gray-200">No active alerts or events</div>
            ) : (
              <div className="space-y-2">
                {filteredEvents.map((evt, idx) => (
                  <div
                    key={idx}
                    className="p-3.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 flex items-start gap-3 shadow-sm"
                  >
                    <AlertTriangle
                      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${evt.severity === 'critical' ? 'text-rose-500' : 'text-amber-500'}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-xs text-gray-900 dark:text-white">{evt.event}</span>
                        {evt.database && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20">
                            {evt.database}
                          </span>
                        )}
                        {evt.time && (
                          <span className="text-[10px] text-gray-200 ml-auto font-mono">
                            {new Date(evt.time).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {evt.details && (
                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed font-mono">{evt.details}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostgresStorageView;
export { PostgresStorageView as PostgresStorage };
